import { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { Box, Icon, Paragraph } from '@zillow/constellation-10';
import { IconDeleteOutline } from '@zillow/constellation-icons';
import {
  type Attachment,
  ATTACHMENT_STATUS,
  AttachmentGuidelines,
  AttachmentWarningModal,
  DeleteAttachmentsButton,
  FILE_TYPES,
  filterAttachments,
  getChatBubbleBorderRadius,
  MessageDocumentList,
  MessageImageGrid,
  useAttachmentWarning,
} from '@zg-rentals/satellite-components';
import StringConstants from '../../constants/StringConstants';
import { useUserWarnings } from '../../hooks/useUserWarnings';
import DeleteAttachments from '../Modals/DeleteAttachments';
import ConditionalWrapper from '../ConditionalWrapper';
import { logError } from '@zg-rentals/log-error';
import { deletedAttachmentsContainerStyles } from './styles';
import {
  emitRachelDownloadAttachmentsEvent,
  RACHEL_DOWNLOAD_ATTACHMENTS_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/attachment';
import {
  emitAttachmentWarningModalImpressionEvent,
  emitViewAttachmentButtonClickEvent,
} from '../../constants/clickstreamEvents/attachmentWarningModal';

const ImagesViewer = dynamic(() => import('../ImagesViewer'), {
  loading: () => null,
  ssr: false,
});

export type AttachmentsForMessage = Array<Attachment>;

type Props = {
  attachments: AttachmentsForMessage;
  conversationId: string;
  isMessageOwner: boolean;
  isMessageSent: boolean;
  messageId: string;
};

const MAX_THUMBNAIL_COUNT = 6;

type AttachmentsRenderError = {
  conversationId: string;
  loggerName: string;
};

export const logAttachmentsRenderError = ({ conversationId, loggerName }: AttachmentsRenderError) => {
  logError({
    error: new Error('logAttachmentsRenderError'),
    context: {
      details: `${loggerName} Error rendering attachment, attachmentMetadataStatus is not complete or url is missing`,
      conversationId,
    },
  });
};

export default function MessageAttachments({
  attachments,
  conversationId,
  isMessageOwner,
  isMessageSent,
  messageId,
}: Props) {
  const { attachmentInterstitialWarningData } = useUserWarnings();
  const { imageAttachments, nonImageAttachments, hasEveryAttachmentExpired, hasSomeDeletedAttachments } = useMemo(
    () => filterAttachments(attachments, FILE_TYPES.IMAGE),
    [attachments],
  );
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [shouldShowAttachmentGuidelinesModal, setShouldShowAttachmentGuidelinesModal] = useState(false);
  const [shouldShowDeleteAttachmentsModal, setShouldShowDeleteAttachmentsModal] = useState(false);
  const [shouldShowImagesViewer, setShouldShowImagesViewer] = useState(false);
  const [shouldLogImageGridError, setShouldLogImageGridError] = useState(false);
  const [shouldLogNonImageError, setShouldLogNonImageError] = useState(false);

  const { isModalOpen, onAttachmentClick, onGoBack, onViewAttachment } = useAttachmentWarning();

  useEffect(() => {
    if (isModalOpen) {
      emitAttachmentWarningModalImpressionEvent();
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (shouldLogImageGridError) {
      logAttachmentsRenderError({
        conversationId: conversationId || '',
        loggerName: 'MessageAttachments_MessageImageGrid',
      });
    }
  }, [shouldLogImageGridError, conversationId]);

  useEffect(() => {
    if (shouldLogNonImageError) {
      logAttachmentsRenderError({
        conversationId: conversationId || '',
        loggerName: 'MessageAttachments_MessageDocumentList',
      });
    }
  }, [shouldLogNonImageError, conversationId]);

  const isDeleteIconDisabled = !isMessageSent || hasEveryAttachmentExpired;
  const hasImageAttachments = imageAttachments.length > 0;
  const hasNonImageAttachments = nonImageAttachments.length > 0;
  const hasAllDeletedAttachments = !hasImageAttachments && !hasNonImageAttachments;
  const shouldShowDeleteIcon = isMessageOwner && !hasAllDeletedAttachments && !hasEveryAttachmentExpired;

  // Create a mutated array to only pass down the max
  // images necessary for rendering in the thumbnail grid
  const imageAttachmentsSubset = [...imageAttachments];
  let overflowImagesCount = 0;
  if (imageAttachments.length > MAX_THUMBNAIL_COUNT) {
    overflowImagesCount = imageAttachments.length - MAX_THUMBNAIL_COUNT;
    imageAttachmentsSubset.length = MAX_THUMBNAIL_COUNT;
  }

  const handleImageThumbnailClick = (zDocId: string): void => {
    const imageIndexSelected = imageAttachments.findIndex((imageAttachment) => imageAttachment.zDocId === zDocId);
    setSelectedImageIndex(imageIndexSelected);
    if (isMessageOwner || !attachmentInterstitialWarningData) {
      setShouldShowImagesViewer(true);
    } else {
      onAttachmentClick(() => setShouldShowImagesViewer(true));
    }
  };

  const handleOpenAttachmentGuidelinesModal = () => {
    setShouldShowAttachmentGuidelinesModal(true);
  };

  const handleOpenDeleteAttachmentsModal = () => {
    setShouldShowDeleteAttachmentsModal(true);
  };
  const handleCloseDeleteAttachmentsModal = () => setShouldShowDeleteAttachmentsModal(false);
  const handleCloseImagesViewer = (): void => setShouldShowImagesViewer(false);

  const handleAttachmentDownloadClickEvent = () => {
    emitRachelDownloadAttachmentsEvent();
  };

  const handleBeforeDocumentAction =
    isMessageOwner || !attachmentInterstitialWarningData ? undefined : onAttachmentClick;

  return (
    <>
      {!isMessageOwner && attachmentInterstitialWarningData && (
        <AttachmentWarningModal
          isOpen={isModalOpen}
          onGoBack={onGoBack}
          onViewAttachment={() => {
            emitViewAttachmentButtonClickEvent();
            onViewAttachment();
          }}
          title={attachmentInterstitialWarningData.title}
          body={attachmentInterstitialWarningData.body}
          linkText={attachmentInterstitialWarningData.linkText}
          linkUrl={attachmentInterstitialWarningData.linkUrl}
          goBackLabel={attachmentInterstitialWarningData.primaryCtaLabel}
          viewAttachmentLabel={attachmentInterstitialWarningData.secondaryCtaLabel}
        />
      )}

      {shouldShowAttachmentGuidelinesModal && (
        <AttachmentGuidelines onClose={() => setShouldShowAttachmentGuidelinesModal(false)} />
      )}

      {shouldShowDeleteAttachmentsModal && (
        <DeleteAttachments
          attachments={[...imageAttachments, ...nonImageAttachments]}
          conversationId={conversationId}
          messageId={messageId}
          onClose={handleCloseDeleteAttachmentsModal}
        />
      )}

      {shouldShowImagesViewer && (
        <ImagesViewer
          imageAttachments={imageAttachments}
          onClose={handleCloseImagesViewer}
          selectedImageIndex={selectedImageIndex}
        />
      )}

      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isMessageOwner ? 'flex-end' : 'flex-start',
          width: '100%',
        }}
      >
        {hasSomeDeletedAttachments && (
          <Box
            css={{
              ...deletedAttachmentsContainerStyles,
              ...getChatBubbleBorderRadius(isMessageOwner),
            }}
          >
            {' '}
            <Icon
              css={{ color: 'border.action.neutral.default', fontSize: '1.4em', marginRight: 'default' }}
              render={<IconDeleteOutline />}
            />
            {/* oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 174px` falls between `4000` (160px) and `5000` (200px) on the numeric sizes scale */}
            <Paragraph textStyle="body-xs" css={{ maxWidth: '174px', color: 'text.subtle' }}>
              One or more attachments were removed for all parties
            </Paragraph>
          </Box>
        )}

        {hasImageAttachments && (
          <ConditionalWrapper
            shouldRenderWrapperElement={shouldShowDeleteIcon}
            wrapper={(children) => (
              <Box css={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end' }}>{children}</Box>
            )}
          >
            <>
              {shouldShowDeleteIcon && !hasNonImageAttachments && (
                <DeleteAttachmentsButton isDisabled={isDeleteIconDisabled} onClick={handleOpenDeleteAttachmentsModal} />
              )}

              <MessageImageGrid
                imageAttachments={imageAttachmentsSubset}
                isMessageOwner={isMessageOwner}
                onClick={handleImageThumbnailClick}
                overflowCount={overflowImagesCount}
                onLogError={() => setShouldLogImageGridError(true)}
                onExpiredDetailsClick={handleOpenAttachmentGuidelinesModal}
                impersonationDetectedValue={ATTACHMENT_STATUS.IMPERSONATION_DETECTED}
                impersonationTooltipText={StringConstants.TOOLTIP.IMPERSONATION_VIEW}
              />
            </>
          </ConditionalWrapper>
        )}

        {hasNonImageAttachments && (
          <ConditionalWrapper
            shouldRenderWrapperElement={shouldShowDeleteIcon}
            wrapper={(children) => (
              <Box css={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end' }}>{children}</Box>
            )}
          >
            <>
              {shouldShowDeleteIcon && (
                <DeleteAttachmentsButton isDisabled={isDeleteIconDisabled} onClick={handleOpenDeleteAttachmentsModal} />
              )}

              <MessageDocumentList
                isMessageOwner={isMessageOwner}
                nonImageAttachments={nonImageAttachments}
                onBeforeAction={handleBeforeDocumentAction}
                onExpiredDetailsClick={handleOpenAttachmentGuidelinesModal}
                onEventTracking={handleAttachmentDownloadClickEvent}
                onLogError={() => setShouldLogNonImageError(true)}
                downloadEventId={RACHEL_DOWNLOAD_ATTACHMENTS_EVENT_TYPE_ID}
              />
            </>
          </ConditionalWrapper>
        )}
      </Box>
    </>
  );
}
